/**
 * gengql documents for the internal accounts-payable console (ENG-5624 / FE7).
 *
 * Platform-wide, not engagements: `sourceType` spans consultations, written
 * responses, response referrals and engagement hours submissions, and the first
 * business use is the consultation payout run Finance does by hand today. The
 * engagement hydration fields below are a detail panel on rows that have them,
 * never a column set the layout depends on.
 *
 * Two things about this surface that shape every document here:
 *
 * - **`Money.cents` is a `String!`.** Parse before arithmetic; never sum across
 *   currencies. `stripeBalance` returns a list per currency for exactly this
 *   reason.
 * - **`PageInfo` carries no `endCursor`.** The cursor for the next page is the
 *   last edge's, so every paged selection set must ask for `cursor` on edges.
 */
import { gengql } from '@/__generated__';

/**
 * The queue. Always status-filtered in practice: the unfiltered sort filesorts
 * the whole table, while a status-pinned query uses idx_xp_status_created
 * (which is only reachable when status pins its leading column).
 *
 * `payeeIdentity` is the ENG-5624 api half — a name, so the operator can read
 * who they are about to pay. `payee` stays the identifier; identity can be null
 * for a payee whose profile is gone, and a null there is never a reason to hide
 * the row, because the obligation outlives the profile.
 */
export const EXPERT_PAYMENTS_QUEUE = gengql(/* GraphQL */ `
  query expertPaymentsQueue(
    $status: ExpertPaymentStatus
    $sourceType: ExpertPaymentSourceType
    $kind: ExpertPaymentKind
    $payee: ID
    $first: Int!
    $after: String
  ) {
    expertPayments(
      status: $status
      sourceType: $sourceType
      kind: $kind
      payee: $payee
      first: $first
      after: $after
    ) {
      pageInfo {
        total
        hasNextPage
      }
      edges {
        cursor
        node {
          id
          payee
          payeeIdentity {
            userId
            name
          }
          kind
          sourceType
          sourceId
          amount {
            cents
            currency
          }
          rail
          status
          providerRef
          description
          reasoning
          failureReason
          attemptCount
          createdBy
          executedBy
          createdAt
          updatedAt
          paidAt
          engagementId
          hoursSubmissionId
          hoursConfirmed
          periodStart
          periodEnd
        }
      }
    }
  }
`);

/**
 * The pre-payout balance preflight. Per currency, never summed across — two
 * entries is two separate answers to "can we cover this run", not one.
 */
export const STRIPE_BALANCE = gengql(/* GraphQL */ `
  query stripeBalance {
    stripeBalance {
      available {
        cents
        currency
      }
      pending {
        cents
        currency
      }
    }
  }
`);

/**
 * The 1099 floor. Paged as of the ENG-5624 api half, but paging bounds the
 * response and not the query: `WHERE YEAR(paid_at) = ?` is non-sargable with no
 * paid_at index, so every call scans the whole table. Run on demand at
 * compliance-report frequency — never polled, never auto-refreshed.
 *
 * `currency` is its own field as well as being inside `total`: two rows can
 * share (payee, year, rail) and differ only by it, which makes it part of the
 * row's identity and not a formatting detail.
 */
export const EXPERT_PAYMENT_LEDGER = gengql(/* GraphQL */ `
  query expertPaymentLedger($year: Int!, $first: Int!, $after: String) {
    expertPaymentLedger(year: $year, first: $first, after: $after) {
      pageInfo {
        total
        hasNextPage
      }
      edges {
        cursor
        node {
          payee
          payeeIdentity {
            userId
            name
          }
          year
          rail
          currency
          count
          total {
            cents
            currency
          }
        }
      }
    }
  }
`);

/** Claim and execute. Fires from `pending` and `failed` only — never `processing`. */
export const PAY_EXPERT_PAYMENT = gengql(/* GraphQL */ `
  mutation payExpertPayment($id: ID!) {
    payExpertPayment(id: $id) {
      id
      status
      providerRef
      failureReason
      attemptCount
      executedBy
      updatedAt
      paidAt
    }
  }
`);

/**
 * Void an obligation. Takes NO reason argument — the UI must not pretend to
 * collect one. `executedBy` on a canceled row is the CANCELLER, so it must
 * never be labelled "executed by" once the row reaches this state.
 */
export const CANCEL_EXPERT_PAYMENT = gengql(/* GraphQL */ `
  mutation cancelExpertPayment($id: ID!) {
    cancelExpertPayment(id: $id) {
      id
      status
      failureReason
      executedBy
      updatedAt
    }
  }
`);

/**
 * Operator-asserted terminal outcome for a stuck `processing` row.
 *
 * `providerRef` is typed nullable at the schema but is REQUIRED when
 * `outcome: paid` — the client replicates that check so the operator finds out
 * before the round trip. `note` is non-null at the schema.
 */
export const RESOLVE_EXPERT_PAYMENT = gengql(/* GraphQL */ `
  mutation resolveExpertPayment(
    $id: ID!
    $outcome: ExpertPaymentResolveOutcome!
    $providerRef: String
    $note: String!
  ) {
    resolveExpertPayment(id: $id, outcome: $outcome, providerRef: $providerRef, note: $note) {
      id
      status
      providerRef
      reasoning
      failureReason
      executedBy
      updatedAt
      paidAt
    }
  }
`);

/**
 * The manual rail: create-and-record in one call, for money that moved off
 * platform. This is the one place an operator types an amount, and it is
 * immutable the moment it lands.
 *
 * The full row is selected back deliberately. A duplicate against a live
 * `(sourceType, sourceId, kind)` triple returns the PRE-EXISTING row rather
 * than the one submitted — silently, with the submitted description dropped —
 * so the UI has to render what came back instead of echoing its own inputs.
 */
export const RECORD_MANUAL_EXPERT_PAYMENT = gengql(/* GraphQL */ `
  mutation recordManualExpertPayment(
    $payeeUserId: ID!
    $kind: ExpertPaymentKind!
    $sourceType: ExpertPaymentSourceType!
    $sourceId: String!
    $amount: MoneyInput!
    $externalRef: String!
    $description: String!
    $reasoning: String
    $paidAt: DateTime
  ) {
    recordManualExpertPayment(
      payeeUserId: $payeeUserId
      kind: $kind
      sourceType: $sourceType
      sourceId: $sourceId
      amount: $amount
      externalRef: $externalRef
      description: $description
      reasoning: $reasoning
      paidAt: $paidAt
    ) {
      id
      payee
      payeeIdentity {
        userId
        name
      }
      kind
      sourceType
      sourceId
      amount {
        cents
        currency
      }
      rail
      status
      providerRef
      description
      reasoning
      paidAt
      createdAt
    }
  }
`);
